// Characters, passengers, Deep Ones, the two Monarchs and the Deep — all procedural meshes.
// Pieces glide to their new positions; monsters rise out of / sink into the sea.
import { Html } from "@react-three/drei";
import { useFrame } from "@react-three/fiber";
import { useMemo, useRef } from "react";
import * as THREE from "three";

import * as uf from "../../../../../common/unfathomable";
import { art } from "../assets";
import { kSeatColors } from "../seatColors";
import css from "../Unfathomable.module.css";
import { kDeckY, kDeepXZ, isWater, monsterAnchor, peopleAnchor } from "./coords";

type Vec3 = [number, number, number];
const easeInOut = (t: number) => (t < 0.5 ? 4 * t * t * t : 1 - (-2 * t + 2) ** 3 / 2);

/** One trip from `from` to `to`: eased, over a time that grows with the distance. */
interface Trip { from: THREE.Vector3; to: THREE.Vector3; t: number; dur: number; dist: number }
function newTrip(at: THREE.Vector3, to: Vec3, durScale: number): Trip {
  const end = new THREE.Vector3(...to);
  const dist = at.distanceTo(end);
  return { from: at.clone(), to: end, t: 0, dur: Math.min(2.2, 0.45 + dist * 0.08) * durScale, dist };
}
/** Advance a trip; writes the eased position (plus a small hop arc) into `out`. Returns progress 0..1. */
function stepTrip(trip: Trip, dt: number, hop: number, out: THREE.Vector3): number {
  trip.t = Math.min(1, trip.t + dt / trip.dur);
  out.lerpVectors(trip.from, trip.to, easeInOut(trip.t));
  out.y += Math.sin(Math.PI * trip.t) * hop * Math.min(1, trip.dist / 2.5);
  return trip.t;
}

/**
 * Travel smoothly to a new target: an eased move with a small hop, timed by the distance (not an
 * instant jump, not an exponential snap). The first target is taken as the starting place.
 */
function useTravel(target: Vec3, { hop = 0.5, durScale = 1 } = {}) {
  const ref = useRef<THREE.Group>(null);
  const trip = useRef<Trip | null>(null);
  const pos = useMemo(() => new THREE.Vector3(...target), []); // eslint-disable-line react-hooks/exhaustive-deps
  useFrame((_, dt) => {
    const g = ref.current;
    if (!g) return;
    const tr = trip.current;
    if (!tr || tr.to.x !== target[0] || tr.to.y !== target[1] || tr.to.z !== target[2]) {
      trip.current = tr ? newTrip(pos, target, durScale) : { ...newTrip(pos, target, durScale), t: 1 };
    }
    if (trip.current!.t < 1) stepTrip(trip.current!, dt, hop, pos);
    else pos.copy(trip.current!.to);
    g.position.copy(pos);
  });
  return ref;
}

/** Grow from nothing when mounted. */
function useGrow(speed = 3) {
  const ref = useRef<THREE.Group>(null);
  const s = useRef(0);
  useFrame((_, dt) => {
    if (!ref.current || s.current >= 1) return;
    s.current = Math.min(1, s.current + dt * speed);
    const e = 1 - Math.pow(1 - s.current, 3);
    ref.current.scale.setScalar(e);
  });
  return ref;
}

// ---------------------------------------------------------------- characters
const pawnProfile = [
  [0, 0], [0.34, 0], [0.34, 0.08], [0.26, 0.14], [0.2, 0.3], [0.15, 0.55], [0.13, 0.7], [0.2, 0.74], [0.2, 0.78], [0.12, 0.8],
  [0.18, 0.9], [0.2, 1.0], [0.16, 1.1], [0.08, 1.15], [0, 1.16],
].map(([x, y]) => new THREE.Vector2(x, y));

function portraitTexture(character: uf.CharacterId, artOk: boolean, color: string): THREE.Texture {
  if (artOk) {
    const t = new THREE.TextureLoader().load(art.token(character));
    t.colorSpace = THREE.SRGBColorSpace;
    return t;
  }
  const c = document.createElement("canvas");
  c.width = c.height = 128;
  const g = c.getContext("2d")!;
  g.fillStyle = "#0b1a1d"; g.beginPath(); g.arc(64, 64, 62, 0, Math.PI * 2); g.fill();
  g.strokeStyle = color; g.lineWidth = 8; g.stroke();
  g.fillStyle = color; g.font = "bold 64px serif"; g.textAlign = "center"; g.textBaseline = "middle";
  g.fillText(uf.kCharacters[character].name[0], 64, 68);
  const t = new THREE.CanvasTexture(c);
  t.colorSpace = THREE.SRGBColorSpace;
  return t;
}

function Pawn({ p, pos, current, you, artOk, labels = true }: { p: uf.PlayerView; pos: [number, number, number]; current: boolean; you: boolean; artOk: boolean; labels?: boolean }) {
  const ref = useTravel(pos, { hop: 0.55 });
  const color = p.revealed ? "#c94040" : kSeatColors[p.seat % kSeatColors.length];
  const lathe = useMemo(() => new THREE.LatheGeometry(pawnProfile, 24), []);
  const tex = useMemo(() => portraitTexture(p.character, artOk, color), [p.character, artOk, color]);
  const ring = useRef<THREE.Mesh>(null);
  const badge = useRef<THREE.Sprite>(null);
  useFrame(({ clock }) => {
    const t = clock.elapsedTime;
    if (ring.current) { ring.current.scale.setScalar(1 + 0.15 * Math.sin(t * 4)); (ring.current.material as THREE.MeshBasicMaterial).opacity = 0.55 + 0.35 * Math.sin(t * 4); }
    if (badge.current) badge.current.position.y = 1.75 + Math.sin(t * 1.6 + p.seat) * 0.06;
  });
  const ch = uf.kCharacters[p.character];
  return (
    <group ref={ref}>
      <mesh geometry={lathe} castShadow>
        <meshStandardMaterial color={color} metalness={0.55} roughness={0.35} />
      </mesh>
      {current && (
        <mesh ref={ring} rotation={[-Math.PI / 2, 0, 0]} position={[0, 0.02, 0]}>
          <ringGeometry args={[0.42, 0.55, 40]} />
          <meshBasicMaterial color="#ffe08a" transparent depthWrite={false} />
        </mesh>
      )}
      <sprite ref={badge} position={[0, 1.75, 0]} scale={[0.9, 0.9, 0.9]}>
        <spriteMaterial map={tex} transparent depthWrite={false} />
      </sprite>
      {labels && (
        <Html position={[0, 2.35, 0]} center distanceFactor={14} zIndexRange={[4, 0]} style={{ pointerEvents: "none" }}>
          <div className={css.pawnLabel} style={{ borderColor: color }}>
            {you ? "★ " : ""}{p.name}<span className={css.pawnSub}>{ch.name}</span>
          </div>
        </Html>
      )}
    </group>
  );
}

function Passenger({ pos }: { pos: [number, number, number] }) {
  const grow = useGrow();
  return (
    <group position={pos}>
      <group ref={grow} scale={0}>
        <mesh position={[0, 0.32, 0]} castShadow>
          <capsuleGeometry args={[0.12, 0.34, 4, 10]} />
          <meshStandardMaterial color="#d8d2c2" roughness={0.8} />
        </mesh>
        <mesh position={[0, 0.66, 0]}>
          <sphereGeometry args={[0.11, 12, 12]} />
          <meshStandardMaterial color="#e8d0b4" roughness={0.7} />
        </mesh>
      </group>
    </group>
  );
}

// ---------------------------------------------------------------- Deep One
function DeepOneFigure({ seed }: { seed: number }) {
  const body = useRef<THREE.Group>(null);
  useFrame(({ clock }) => {
    const t = clock.elapsedTime + seed;
    if (body.current) { body.current.rotation.y = Math.sin(t * 0.8) * 0.5; body.current.rotation.z = Math.sin(t * 1.7) * 0.06; }
  });
  const skin = "#3f8f72";
  return (
    <group ref={body}>
      {/* hunched torso and legs */}
      <mesh position={[0, 0.55, 0]} rotation={[0.25, 0, 0]} castShadow>
        <capsuleGeometry args={[0.2, 0.45, 4, 10]} />
        <meshStandardMaterial color={skin} roughness={0.45} metalness={0.15} />
      </mesh>
      <mesh position={[-0.1, 0.15, 0]}><capsuleGeometry args={[0.07, 0.25, 3, 6]} /><meshStandardMaterial color={skin} /></mesh>
      <mesh position={[0.1, 0.15, 0]}><capsuleGeometry args={[0.07, 0.25, 3, 6]} /><meshStandardMaterial color={skin} /></mesh>
      {/* long arms with claws */}
      <mesh position={[-0.26, 0.55, 0.12]} rotation={[0.8, 0, 0.3]}><capsuleGeometry args={[0.055, 0.42, 3, 6]} /><meshStandardMaterial color={skin} /></mesh>
      <mesh position={[0.26, 0.55, 0.12]} rotation={[0.8, 0, -0.3]}><capsuleGeometry args={[0.055, 0.42, 3, 6]} /><meshStandardMaterial color={skin} /></mesh>
      {/* fish head with crest */}
      <mesh position={[0, 0.98, 0.1]} scale={[1, 0.9, 1.2]} castShadow>
        <sphereGeometry args={[0.2, 16, 12]} />
        <meshStandardMaterial color="#58a98a" roughness={0.35} />
      </mesh>
      {[-0.08, 0, 0.08].map((z, i) => (
        <mesh key={i} position={[0, 1.18 - Math.abs(z), 0.02 + z]} rotation={[0.2, 0, 0]}>
          <coneGeometry args={[0.04, 0.2, 5]} />
          <meshStandardMaterial color="#2e6e58" />
        </mesh>
      ))}
      <mesh position={[-0.09, 1.0, 0.3]}><sphereGeometry args={[0.055, 10, 10]} /><meshStandardMaterial color="#f3ffb0" emissive="#e8ff7a" emissiveIntensity={1.6} /></mesh>
      <mesh position={[0.09, 1.0, 0.3]}><sphereGeometry args={[0.055, 10, 10]} /><meshStandardMaterial color="#f3ffb0" emissive="#e8ff7a" emissiveIntensity={1.6} /></mesh>
    </group>
  );
}

/** Where the Deep Ones of one space stand (around its anchor); beyond these the badge counts them. */
const kSlots: [number, number][] = [
  [0, 0], [0.55, 0.25], [-0.5, 0.3], [0.1, -0.45], [-0.55, -0.35], [0.62, -0.32], [0.05, 0.62], [-1.0, 0.0], [1.05, 0.05],
];

/** One Deep One on the board, followed from space to space. */
interface Figure { id: number; sp: uf.SpaceId; idx: number }

/**
 * Turn per-space Deep One counts into individual figures that keep their identity: when counts
 * change, figures leaving a space are matched to the nearest space gaining one (so they walk
 * there); unmatched gains are newcomers, unmatched losses are gone (defeated / back to the sea).
 */
function useDeepOneFigures(view: uf.GameView): Figure[] {
  const figs = useRef<{ id: number; sp: uf.SpaceId }[] | null>(null);
  const nextId = useRef(1);
  const spaces = Object.keys(view.spaces) as uf.SpaceId[];
  const key = spaces.map(sp => view.spaces[sp].deepOnes).join(",");
  return useMemo(() => {
    const want = new Map(spaces.map(sp => [sp, view.spaces[sp].deepOnes] as const));
    const keep: { id: number; sp: uf.SpaceId }[] = [];
    const movers: { id: number; sp: uf.SpaceId }[] = [];
    const have = new Map<uf.SpaceId, number>();
    for (const f of figs.current ?? []) {
      const c = have.get(f.sp) ?? 0;
      if (c < (want.get(f.sp) ?? 0)) { keep.push(f); have.set(f.sp, c + 1); } else movers.push(f);
    }
    for (const [sp, n] of want) {
      const [x, z] = monsterAnchor(sp);
      for (let c = have.get(sp) ?? 0; c < n; c++) {
        let best = -1, bestD = Infinity;
        movers.forEach((m, i) => {
          const [mx, mz] = monsterAnchor(m.sp);
          const d = Math.hypot(mx - x, mz - z);
          if (d < bestD) { bestD = d; best = i; }
        });
        if (best >= 0) keep.push({ id: movers.splice(best, 1)[0].id, sp });
        else keep.push({ id: nextId.current++, sp });
      }
    }
    figs.current = keep;
    const idx = new Map<uf.SpaceId, number>();
    return keep.map(f => { const i = idx.get(f.sp) ?? 0; idx.set(f.sp, i + 1); return { ...f, idx: i }; });
  }, [key]); // eslint-disable-line react-hooks/exhaustive-deps
}

function slotOf(f: Figure): Vec3 {
  const [x, z] = monsterAnchor(f.sp);
  const [dx, dz] = kSlots[f.idx % kSlots.length];
  return [x + dx, isWater(f.sp) ? -0.35 : kDeckY, z + dz];
}

/**
 * A Deep One walking between spaces. Positions are in ship coordinates; on deck it follows the
 * ship's rocking (`rock`, the ship's moving group), in the water it bobs instead — blended over
 * the trip, so climbing aboard is one smooth motion.
 */
function DeepOneMover({ f, rock }: { f: Figure; rock?: React.RefObject<THREE.Group> }) {
  const ref = useRef<THREE.Group>(null);
  const face = useRef<THREE.Group>(null);
  const grow = useGrow(2);
  const target = slotOf(f);
  const deckNow = useRef(isWater(f.sp) ? 0 : 1);
  const trip = useRef<(Trip & { fromDeck: number; toDeck: number }) | null>(null);
  const pos = useMemo(() => new THREE.Vector3(...target), []); // eslint-disable-line react-hooks/exhaustive-deps
  const tmp = useMemo(() => new THREE.Vector3(), []);
  useFrame(({ clock }, dt) => {
    const g = ref.current;
    if (!g) return;
    const toDeck = isWater(f.sp) ? 0 : 1;
    const tr = trip.current;
    if (!tr || tr.to.x !== target[0] || tr.to.y !== target[1] || tr.to.z !== target[2]) {
      const t = newTrip(pos, target, 1.6);
      trip.current = { ...t, t: tr ? 0 : 1, fromDeck: deckNow.current, toDeck };
      if (tr && face.current && t.dist > 0.3) face.current.rotation.y = Math.atan2(target[0] - pos.x, target[2] - pos.z);
    }
    const cur = trip.current!;
    if (cur.t < 1) {
      const p = stepTrip(cur, dt, 0.8, pos);
      deckNow.current = cur.fromDeck + (cur.toDeck - cur.fromDeck) * easeInOut(p);
    } else { pos.copy(cur.to); deckNow.current = cur.toDeck; }
    const w = deckNow.current;
    tmp.copy(pos);
    tmp.y += Math.sin(clock.elapsedTime * 1.3 + f.id) * 0.12 * (1 - w);
    if (rock?.current && w > 0) tmp.lerp(pos.clone().applyMatrix4(rock.current.matrix), w);
    g.position.copy(tmp);
  });
  return (
    <group ref={ref} visible={f.idx < kSlots.length}>
      <group ref={grow} scale={0}>
        <group ref={face} rotation={[0, Math.PI + f.id, 0]}><DeepOneFigure seed={f.id * 1.7} /></group>
      </group>
    </group>
  );
}

// ---------------------------------------------------------------- Monarchs
function Hydra() {
  const tentacles = useRef<THREE.Group>(null);
  const chains = useMemo(() => Array.from({ length: 7 }, (_, i) => ({ a: (i / 7) * Math.PI * 2, len: 9 + (i % 3) })), []);
  useFrame(({ clock }) => {
    const g = tentacles.current;
    if (!g) return;
    const t = clock.elapsedTime;
    g.children.forEach((chain, ci) => {
      chain.children.forEach((seg, si) => {
        const k = si / 9;
        seg.position.set(Math.sin(t * 1.3 + si * 0.5 + ci) * k * 0.7, si * 0.32, Math.cos(t * 1.1 + si * 0.4 + ci * 2) * k * 0.5);
      });
    });
  });
  return (
    <group>
      <mesh position={[0, 0.6, 0]} scale={[1.6, 1.1, 1.4]} castShadow>
        <sphereGeometry args={[1, 24, 18]} />
        <meshStandardMaterial color="#7a1f3a" roughness={0.3} metalness={0.2} emissive="#3a0612" />
      </mesh>
      {[-0.45, 0, 0.45].map((x, i) => (
        <mesh key={i} position={[x, 1.3 - Math.abs(x) * 0.3, 1.15]}>
          <sphereGeometry args={[0.16, 12, 12]} />
          <meshStandardMaterial color="#ffd1a8" emissive="#ff6a3a" emissiveIntensity={2.5} />
        </mesh>
      ))}
      <group ref={tentacles}>
        {chains.map((c, ci) => (
          <group key={ci} position={[Math.cos(c.a) * 1.3, 0.2, Math.sin(c.a) * 1.1]} rotation={[0, 0, Math.cos(c.a) * 0.4]}>
            {Array.from({ length: c.len }).map((_, si) => (
              <mesh key={si}>
                <sphereGeometry args={[0.32 * (1 - si / (c.len + 1)), 10, 8]} />
                <meshStandardMaterial color={si % 2 ? "#8e2a48" : "#a33a58"} roughness={0.35} />
              </mesh>
            ))}
          </group>
        ))}
      </group>
      <pointLight position={[0, 2, 1.5]} color="#ff4a6a" intensity={10} distance={9} />
    </group>
  );
}

function Dagon() {
  const head = useRef<THREE.Group>(null);
  useFrame(({ clock }) => {
    if (head.current) head.current.rotation.y = Math.sin(clock.elapsedTime * 0.4) * 0.4;
  });
  return (
    <group ref={head}>
      <mesh position={[0, 0.8, 0]} scale={[1.3, 1.6, 1.1]} castShadow>
        <sphereGeometry args={[1, 24, 18]} />
        <meshStandardMaterial color="#2f6b4a" roughness={0.35} metalness={0.2} emissive="#08200f" />
      </mesh>
      <mesh position={[0, 2.25, 0.35]} scale={[1.05, 0.95, 1.3]} castShadow>
        <sphereGeometry args={[0.85, 24, 18]} />
        <meshStandardMaterial color="#3f8a60" roughness={0.3} />
      </mesh>
      {Array.from({ length: 7 }).map((_, i) => (
        <mesh key={i} position={[0, 3.0 - Math.abs(i - 3) * 0.12, -0.3 + (i - 3) * 0.22]} rotation={[0.3 + (i - 3) * 0.12, 0, 0]}>
          <coneGeometry args={[0.13, 0.9 - Math.abs(i - 3) * 0.12, 6]} />
          <meshStandardMaterial color="#c9a227" metalness={0.8} roughness={0.25} />
        </mesh>
      ))}
      {[-0.35, 0.35].map((x, i) => (
        <mesh key={i} position={[x, 2.35, 1.2]}>
          <sphereGeometry args={[0.17, 12, 12]} />
          <meshStandardMaterial color="#fff6b0" emissive="#ffe84a" emissiveIntensity={3} />
        </mesh>
      ))}
      {[-1, 1].map(s => (
        <mesh key={s} position={[s * 1.45, 1.6, 0.4]} rotation={[0.4, 0, -s * 0.9]} castShadow>
          <capsuleGeometry args={[0.22, 1.6, 4, 10]} />
          <meshStandardMaterial color="#2f6b4a" roughness={0.4} />
        </mesh>
      ))}
      <pointLight position={[0, 2.4, 2]} color="#c8ff6a" intensity={10} distance={9} />
    </group>
  );
}

function Monarch({ who, at }: { who: "hydra" | "dagon"; at: uf.SpaceId | "deep" }) {
  const deep = at === "deep";
  const [x, z] = deep ? kDeepXZ : monsterAnchor(at as uf.SpaceId);
  const off = who === "hydra" ? -1.6 : 1.6;
  const target: [number, number, number] = deep ? [x + off, -7, z] : [x + off, -0.5, z + (z < 0 ? -1.2 : 1.2)];
  const ref = useTravel(target, { hop: 0, durScale: 2.2 });
  return (
    <group ref={ref}>
      <group scale={0.85}>{who === "hydra" ? <Hydra /> : <Dagon />}</group>
      {!deep && (
        <Html position={[0, 4.4, 0]} center distanceFactor={14} zIndexRange={[4, 0]} style={{ pointerEvents: "none" }}>
          <div className={css.monarchBadge} data-who={who}>{who === "hydra" ? "母神海德拉" : "父神大衮"}</div>
        </Html>
      )}
    </group>
  );
}

function DeepPool({ n, monarchs }: { n: number; monarchs: number }) {
  const ref = useRef<THREE.Group>(null);
  const halo = useRef<THREE.MeshBasicMaterial>(null);
  const core = useRef<THREE.MeshBasicMaterial>(null);
  const light = useRef<THREE.PointLight>(null);
  useFrame(({ clock }, dt) => {
    if (ref.current) ref.current.rotation.y += dt * (0.4 + n * 0.08);
    // A slow, eerie breathing glow — brighter the more lurks below.
    const t = clock.elapsedTime;
    const pulse = 0.5 + 0.5 * Math.sin(t * 1.3);
    const strength = 0.55 + Math.min(0.45, (n + monarchs * 2) * 0.05);
    if (halo.current) halo.current.opacity = (0.35 + 0.35 * pulse) * strength;
    if (core.current) core.current.opacity = (0.18 + 0.14 * Math.sin(t * 2.1 + 1)) * strength;
    if (light.current) light.current.intensity = (3 + 3 * pulse) * strength;
  });
  const [x, z] = kDeepXZ;
  return (
    <group position={[x, 0.32, z]}>
      {/* eerie green halo around the whirlpool */}
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, 0.02, 0]}>
        <ringGeometry args={[3.3, 4.6, 64]} />
        <meshBasicMaterial ref={halo} color="#19e07a" transparent opacity={0.5} blending={THREE.AdditiveBlending} depthWrite={false} fog={false} toneMapped={false} />
      </mesh>
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, 0.03, 0]}>
        <circleGeometry args={[1.6, 40]} />
        <meshBasicMaterial ref={core} color="#12b865" transparent opacity={0.25} blending={THREE.AdditiveBlending} depthWrite={false} fog={false} toneMapped={false} />
      </mesh>
      <pointLight ref={light} position={[0, 1.2, 0]} color="#3dffb0" intensity={4} distance={11} decay={2} />
      <group ref={ref}>
        {[1.4, 2.1, 2.8, 3.5].map((r, i) => (
          <mesh key={i} rotation={[-Math.PI / 2, 0, i * 0.7]}>
            <ringGeometry args={[r - 0.18, r, 48, 1, 0, Math.PI * 1.4]} />
            <meshBasicMaterial color="#0b3b3a" transparent opacity={0.55 - i * 0.1} depthWrite={false} />
          </mesh>
        ))}
        <mesh rotation={[-Math.PI / 2, 0, 0]}>
          <circleGeometry args={[1.3, 40]} />
          <meshBasicMaterial color="#020a0c" transparent opacity={0.85} depthWrite={false} />
        </mesh>
      </group>
      <Html position={[0, 1.2, 0]} center distanceFactor={14} zIndexRange={[4, 0]} style={{ pointerEvents: "none" }}>
        <div className={css.monsterBadge}>深渊{n > 0 ? ` · 深潜者 ×${n}` : ""}{monarchs > 0 ? ` · 君主 ${monarchs}` : ""}</div>
      </Html>
    </group>
  );
}

// ---------------------------------------------------------------- all pieces
/**
 * `part` splits the pieces between parents:
 * - "ship": characters and passengers, placed inside the rocking ship;
 * - "sea": the Monarchs, the Deep and the Deep Ones — all of them when `rock` (the ship's moving
 *   group) is given, so they can walk from the water onto the deck; otherwise only those in the water;
 * - "bow" / "stern": what stands on that half of the ship in the shipwreck (no floating labels:
 *   they would not sink).
 */
export function Pieces({ view, artOk, part, rock }: {
  view: uf.GameView; artOk: boolean; part?: "ship" | "sea" | "bow" | "stern"; rock?: React.RefObject<THREE.Group>;
}) {
  const onShip = (x: number) => part === undefined || part === "ship" || (part === "bow" ? x < 0 : part === "stern" ? x >= 0 : false);
  const inSea = part === undefined || part === "sea";
  const labels = part === undefined || part === "ship" || part === "sea";
  const figures = useDeepOneFigures(view);
  const showFigure = (f: Figure) => part === "ship" ? false
    : part === undefined ? true
    : part === "sea" ? (!!rock || isWater(f.sp))
    : !isWater(f.sp) && onShip(monsterAnchor(f.sp)[0]);
  const spaces = Object.keys(view.spaces) as uf.SpaceId[];
  const bySpace = new Map<uf.SpaceId, uf.PlayerView[]>();
  for (const p of view.players) bySpace.set(p.space, [...(bySpace.get(p.space) ?? []), p]);
  const pawns: { p: uf.PlayerView; pos: [number, number, number] }[] = [];
  for (const [sp, ps] of bySpace) {
    const [ax, az] = peopleAnchor(sp);
    const per = sp === "sickbay" || sp === "brig" ? 2 : 4;
    ps.forEach((p, i) => {
      const col = i % per, row = Math.floor(i / per);
      pawns.push({ p, pos: [ax + (col - (Math.min(ps.length, per) - 1) / 2) * 0.78, kDeckY, az + row * 0.75] });
    });
  }
  const monarchsDeep = (view.hydra === "deep" ? 1 : 0) + (view.dagon === "deep" ? 1 : 0);
  return (
    <group>
      {part !== "sea" && pawns.filter(({ pos }) => onShip(pos[0])).map(({ p, pos }) => <Pawn key={p.seat} p={p} pos={pos} current={p.seat === view.current} you={p.seat === view.you} artOk={artOk} labels={labels} />)}
      {figures.filter(showFigure).map(f => <DeepOneMover key={f.id} f={f} rock={rock} />)}
      {inSea && labels && spaces.filter(sp => view.spaces[sp].deepOnes > 1).map(sp => {
        const [x, z] = monsterAnchor(sp);
        return (
          <Html key={`dob-${sp}`} position={[x, (isWater(sp) ? -0.35 : kDeckY) + 1.9, z]} center distanceFactor={14} zIndexRange={[4, 0]} style={{ pointerEvents: "none" }}>
            <div className={css.monsterBadge}>深潜者 ×{view.spaces[sp].deepOnes}</div>
          </Html>
        );
      })}
      {part !== "sea" && spaces.filter(sp => view.spaces[sp].passengers > 0 && onShip(monsterAnchor(sp)[0])).flatMap(sp => {
        const [x, z] = monsterAnchor(sp);
        const n = view.spaces[sp].passengers;
        return Array.from({ length: Math.min(n, 4) }, (_, i) => (
          <Passenger key={`pa-${sp}-${i}`} pos={[x - 1.0 + (i % 2) * 0.35, kDeckY, z + (Math.floor(i / 2) - 0.5) * 0.35]} />
        )).concat(n > 1 && labels ? [
          <Html key={`pab-${sp}`} position={[x - 0.85, kDeckY + 1.2, z]} center distanceFactor={14} zIndexRange={[4, 0]} style={{ pointerEvents: "none" }}>
            <div className={css.passengerBadge}>乘客 ×{n}</div>
          </Html>,
        ] : []);
      })}
      {inSea && <Monarch who="hydra" at={view.hydra} />}
      {inSea && <Monarch who="dagon" at={view.dagon} />}
      {inSea && <DeepPool n={view.deepOnesInDeep} monarchs={monarchsDeep} />}
    </group>
  );
}
